// deps
import cx from "classnames";
import React, { memo, ReactElement, useCallback, useMemo, useRef, useState } from "react";

// internal-deps
import { isNonEmptyString } from "@swiggy-private/payment-core";
import { AriaHidden, ElementObserver, useEffectOnce } from "@swiggy-private/core-ui";

// interfaces
import { PaymentOptionProps } from "@payment-ui/interfaces/MorePaymentOptions";
import { PaymentGroupData, PaymentGroupMeta, PaymentMethod, PaymentMethodMeta } from "@payment-ui/interfaces/Payment";

// helpers
import {
    getPaymentOptionIconUrl,
    getPaymentOptionSubtext,
    getPaymentOptionGroupDisplayName,
    getPaymentOptionGroupName,
    getPaymentOptionAlertMessage,
} from "@payment-ui/helpers/MorePaymentOptions";
import { isPaymentGroupEnabled } from "@payment-ui/helpers/Payment";

// widgets
import {
    PaymentMethodIcon as PaymentOptionIcon,
    PaymentMethodIconProps as PaymentOptionIconProps,
} from "@payment-ui/widgets/payment-method-icon";
import { ArrowSvgIcon } from "@payment-ui/widgets/arrow-svg-icon";

// test-ids
import { getMorePaymentOptionTestIds } from "@swiggy-private/payment-test-ids";

// hooks
import { useTooltip } from "./useTooltip";

// styles
import styles from "./PaymentOption.v4.scss";
import { TooltipContainer } from "./styles";

const PAYMENT_OPTION_ICON_WIDTH = 24;

function PaymentOption(
    props: PaymentOptionProps<PaymentGroupData<PaymentGroupMeta, PaymentMethod<PaymentMethodMeta>>>,
): ReactElement {
    const {
        groupData,
        onViewEnter,
        onViewTooltip,
        onClick,
        otherProps,
        isLastElement,
        useMethodForDisplayName = false,
    } = props;

    const [inViewPort, setInViewPort] = useState(false);
    const rafRef = useRef(0);
    const iconUrl = useMemo(() => getPaymentOptionIconUrl(groupData), [groupData]);
    const displayName = useMemo(() => getPaymentOptionGroupDisplayName(groupData), [groupData]);
    const methodDisplayName = useMemo(() => groupData?.paymentMethods[0]?.displayName, [groupData?.paymentMethods]);
    const groupName = useMemo(() => getPaymentOptionGroupName(groupData), [groupData]);
    const isPaymentOptionEnabled = useMemo(() => isPaymentGroupEnabled(groupData), [groupData]);

    const { showTooltip, hideTooltip, tooltipText } = useTooltip({
        paymentGroupData: groupData,
        otherProps: otherProps,
        inViewPort,
        onViewTooltip: onViewTooltip,
    });

    // If PG is disabled, we prioritize to show the disable message otherwise we will show the sub text
    // Ref: https://swiggy.slack.com/archives/C03V4QXUZ6X/p1675157997991399?thread_ts=1674803626.294609&cid=C03V4QXUZ6X
    const subtext = useMemo(() => {
        if (!isPaymentOptionEnabled) {
            return getPaymentOptionAlertMessage(props);
        }
        return getPaymentOptionSubtext(groupData);
    }, [groupData, isPaymentOptionEnabled, props]);

    const containerClass = useMemo(
        () =>
            cx(styles.container, {
                [styles.containerDisabled]: !isPaymentOptionEnabled,
                [styles.containerWithTooltip]: showTooltip,
            }),
        [isPaymentOptionEnabled, showTooltip],
    );

    const {
        container: paymentOptionContainerTestId,
        header: paymentOptionHeaderTestId,
        subtext: paymentOptionSubtextTestId,
        icon: paymentOptionIconTestId,
    } = useMemo(() => getMorePaymentOptionTestIds(groupName), [groupName]);

    //Click onView Command
    const onView = useCallback((): void => {
        onViewEnter?.(groupName);

        if (!rafRef.current) {
            rafRef.current = requestAnimationFrame(() => {
                setInViewPort(true);
            });
        }
    }, [groupName, onViewEnter]);

    let iconProps: PaymentOptionIconProps;

    const onOptionClick = useCallback(() => {
        if (!isPaymentOptionEnabled) {
            return;
        }
        onClick(groupData);
    }, [groupData, isPaymentOptionEnabled, onClick]);

    if (!isNonEmptyString(iconUrl)) {
        iconProps = {
            className: styles.containerIconWithoutUrl,
            parentClassName: styles.containerIconParent,
            "data-testid": paymentOptionIconTestId,
        };
    } else {
        iconProps = {
            src: iconUrl,
            width: PAYMENT_OPTION_ICON_WIDTH,
            imgClassName: styles.containerIconWithUrl,
            parentClassName: styles.containerIconParent,
            "data-testid": paymentOptionIconTestId,
        };
    }

    const elementObserverClass = useMemo(() => {
        return cx({ [styles.containerBorder]: !isLastElement });
    }, [isLastElement]);

    useEffectOnce(() => {
        return () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
        };
    });

    return (
        <ElementObserver
            onViewEnter={onView}
            className={elementObserverClass}
            onClick={onOptionClick}
            data-testid={paymentOptionContainerTestId}
        >
            <div className={containerClass}>
                {showTooltip && <TooltipContainer onClickClose={hideTooltip}>{tooltipText}</TooltipContainer>}
                <PaymentOptionIcon {...iconProps} />
                <div
                    className={styles.containerTextWrapper}
                    role="button"
                    tabIndex={0}
                    aria-label={`${displayName}. ${isNonEmptyString(subtext) ? subtext : ""}`}
                >
                    <AriaHidden as="div" className={styles.containerText} data-testid={paymentOptionHeaderTestId}>
                        {useMethodForDisplayName ? methodDisplayName : displayName}
                    </AriaHidden>
                    {isNonEmptyString(subtext) && (
                        <AriaHidden
                            as="div"
                            className={styles.containerSubtext}
                            data-testid={paymentOptionSubtextTestId}
                        >
                            {subtext}
                        </AriaHidden>
                    )}
                </div>
                <AriaHidden as="div" className={styles.containerArrowIconWrapper}>
                    <ArrowSvgIcon className={styles.containerArrowIcon} />
                </AriaHidden>
            </div>
        </ElementObserver>
    );
}

export default memo(PaymentOption);
